// /app/frontend/src/contexts/PortalContext.jsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { clientsAPI } from '../services/api';

const PortalContext = createContext(null);

// Check if current path is a public route that shouldn't use portal mode
function isPublicRoute() {
  const path = window.location.pathname;
  const publicPaths = [
    '/intake/',           // DPIA Intake forms
    '/questionnaire/',    // Questionnaire responses
    '/assessment/',       // Public assessments
    '/login',             // Login page
    '/register',          // Register page
  ];
  return publicPaths.some(p => path.startsWith(p));
}

// Extract subdomain from hostname or session storage
function getSubdomain() {
  // Skip portal detection for public routes - always return null
  if (isPublicRoute()) {
    console.log('[PortalContext] Public route detected, skipping portal mode');
    return null;
  }
  
  // First check session storage (persists portal mode after login)
  const storedSubdomain = sessionStorage.getItem('portal_subdomain');
  if (storedSubdomain) {
    console.log('[PortalContext] Using stored subdomain:', storedSubdomain);
    return storedSubdomain;
  }
  
  const hostname = window.location.hostname;
  console.log('[PortalContext] Checking hostname:', hostname);
  
  // Check for ?portal=subdomain query param (works on any host for testing)
  const params = new URLSearchParams(window.location.search);
  const portalParam = params.get('portal');
  if (portalParam) {
    console.log('[PortalContext] Portal param found:', portalParam);
    sessionStorage.setItem('portal_subdomain', portalParam);
    return portalParam;
  }
  
  // Handle localhost (no subdomain)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    console.log('[PortalContext] Localhost detected, no portal');
    return null;
  }
  
  // Handle ALL Emergent URLs: anything containing 'emergentagent.com' or 'emergent.host'
  if (hostname.includes('emergentagent.com') || hostname.includes('emergent.host')) {
    const parts = hostname.split('.');
    console.log('[PortalContext] Emergent domain detected, parts:', parts.length, 'hostname:', hostname);
    
    // Preview URLs: *.preview.emergentagent.com
    // Main app: appname.preview.emergentagent.com (4 parts)
    // Portal: client.appname.preview.emergentagent.com (5 parts)
    if (hostname.includes('.preview.emergentagent.com')) {
      if (parts.length >= 5) {
        const subdomain = parts[0];
        console.log('[PortalContext] Preview portal detected:', subdomain);
        sessionStorage.setItem('portal_subdomain', subdomain);
        return subdomain;
      }
      console.log('[PortalContext] Preview main app, no portal');
      return null;
    }
    
    // Production URLs on emergent.host: *.emergent.host
    // Main app: appname.emergent.host (3 parts) - e.g., lia-report-gen.emergent.host
    // Portal: client.appname.emergent.host (4 parts)
    if (hostname.endsWith('.emergent.host')) {
      if (parts.length >= 4) {
        const subdomain = parts[0];
        console.log('[PortalContext] Production portal detected:', subdomain);
        sessionStorage.setItem('portal_subdomain', subdomain);
        return subdomain;
      }
      console.log('[PortalContext] Production main app (.emergent.host), no portal');
      return null;
    }
    
    // Production URLs on emergentagent.com: *.emergentagent.com (no 'preview')
    // Main app: appname.emergentagent.com (3 parts)
    // Portal: client.appname.emergentagent.com (4 parts)
    if (hostname.endsWith('.emergentagent.com') && !hostname.includes('preview')) {
      if (parts.length >= 4) {
        const subdomain = parts[0];
        console.log('[PortalContext] Production portal detected:', subdomain);
        sessionStorage.setItem('portal_subdomain', subdomain);
        return subdomain;
      }
      console.log('[PortalContext] Production main app (.emergentagent.com), no portal');
      return null;
    }
    
    console.log('[PortalContext] Emergent main app, no portal');
    return null;
  }
  
  // For any other domain, default to NO portal mode
  // This prevents "Portal Not Found" errors on unknown domains
  console.log('[PortalContext] Unknown domain, defaulting to no portal');
  return null;
}

export function PortalProvider({ children }) {
  const [subdomain, setSubdomain] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [isPortalMode, setIsPortalMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Clear any stale portal data on initial load for public routes
    if (isPublicRoute()) {
      sessionStorage.removeItem('portal_subdomain');
      setIsPortalMode(false);
      setIsLoading(false);
      console.log('[PortalContext] Public route - cleared portal mode');
      return;
    }
    
    const detectedSubdomain = getSubdomain();
    console.log('[PortalContext] Detected subdomain:', detectedSubdomain);
    
    if (detectedSubdomain) {
      setSubdomain(detectedSubdomain);
      
      // Fetch client info for the portal
      clientsAPI.getBySubdomain(detectedSubdomain)
        .then(response => {
          setClientInfo(response.data);
          setIsPortalMode(true);  // Only set portal mode AFTER successful API call
          setIsLoading(false);
          console.log('[PortalContext] Portal client loaded:', response.data?.company_name);
        })
        .catch(err => {
          console.error('[PortalContext] Failed to load portal:', err);
          // Clear invalid subdomain from storage and disable portal mode
          sessionStorage.removeItem('portal_subdomain');
          setSubdomain(null);
          setIsPortalMode(false);  // Disable portal mode on error
          setError(null);  // Don't set error - just fall back to normal mode
          setIsLoading(false);
        });
    } else {
      setIsPortalMode(false);
      setIsLoading(false);
    }
  }, []);

  // Function to exit portal mode (for DPO switching views)
  const exitPortalMode = () => {
    sessionStorage.removeItem('portal_subdomain');
    setSubdomain(null);
    setClientInfo(null);
    setIsPortalMode(false);
    window.location.href = '/login';
  };

  const value = {
    subdomain,
    clientInfo,
    isPortalMode,
    isLoading,
    error,
    exitPortalMode,
    // Branding helpers
    getBrandingColor: (type = 'primary') => {
      if (!clientInfo?.custom_branding) return type === 'primary' ? '#3B82F6' : '#10B981';
      return clientInfo.custom_branding[`${type}_color`] || (type === 'primary' ? '#3B82F6' : '#10B981');
    },
    getLogo: () => clientInfo?.custom_branding?.logo_url || null,
  };

  return (
    <PortalContext.Provider value={value}>
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortal must be used within a PortalProvider');
  }
  return context;
}

export default PortalContext;
